import { useMutation } from "@tanstack/react-query"
import { createFileRoute, Outlet, redirect, useParams } from "@tanstack/react-router"

import { getTeamsShell } from "@/app/rpc"
import { teamsShellProps } from "@/app/teams/shell-props"
import { getVoiceAccess } from "@/assistant/rpc/voice-access"
import { VoiceAssistantMount } from "@/assistant/voice"
import { useLogout } from "@/auth/hooks/use-logout"
import { getUser, requireUserId } from "@/auth/rpc"
import { AppShell } from "@/core/components/app-shell"

/** Pathless layout: every route below it needs a signed-in user and renders inside the shell. */
export const Route = createFileRoute("/_app-shell")({
  beforeLoad: async ({ location }) => {
    await requireUserId({ data: { redirectTo: location.href } })
  },
  loader: async ({ location }) => {
    const [data, teams, voice] = await Promise.all([
      getUser(),
      getTeamsShell(),
      getVoiceAccess().catch(() => ({ logbook: false, receipts: false })),
    ])
    // the session can end between beforeLoad and here
    if (!data.user) throw redirect({ to: "/login", search: { redirectTo: location.href } })
    return { ...data, teams, voice }
  },
  head: () => ({ meta: [{ name: "robots", content: "noindex" }] }),
  component: AppLayout,
})

function AppLayout() {
  const { user, teams, voice } = Route.useLoaderData()
  // only the /team/$orgId/... routes have this param
  const { orgId } = useParams({ strict: false }) as { orgId?: string }
  const logout = useLogout()
  // logout follows the server redirect to "/" and resets the query cache. Failures show in the shell.
  const signOut = useMutation({ mutationFn: logout })

  return (
    <>
      <AppShell
        email={user?.email ?? ""}
        name={user?.name}
        {...teamsShellProps(teams, orgId)}
        onSignOut={() => signOut.mutate()}
        isSigningOut={signOut.isPending}
        signOutError={signOut.error ? "Couldn't sign out. Try again." : undefined}
      >
        <Outlet />
      </AppShell>
      {!orgId && (voice.logbook || voice.receipts) ? <VoiceAssistantMount /> : null}
    </>
  )
}
